import { forwardRef } from "react";
import * as RC from "@radix-ui/react-checkbox";
import { Check, Minus } from "lucide-react";
import clsx from "clsx";

export type CheckboxProps = {
  label?: string;
  hint?: string;
  error?: string;
  indeterminate?: boolean;
} & RC.CheckboxProps;

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox(
  { label, hint, error, indeterminate, className, id, ...rest },
  ref,
) {
  const cid = id ?? rest.name ?? `cb-${Math.random().toString(36).slice(2, 8)}`;
  const checked = indeterminate ? "indeterminate" : rest.checked;
  return (
    <label htmlFor={cid} className="inline-flex items-start gap-2 text-sm leading-5 cursor-pointer">
      <RC.Root
        ref={ref}
        id={cid}
        checked={checked}
        className={clsx(
          "size-4 mt-0.5 rounded-[var(--radius-sm)] border flex items-center justify-center transition-colors",
          "border-[var(--color-border-default)] bg-[var(--color-surface-base)]",
          "data-[state=checked]:bg-[var(--color-accent)] data-[state=checked]:border-[var(--color-accent)]",
          "data-[state=indeterminate]:bg-[var(--color-accent)] data-[state=indeterminate]:border-[var(--color-accent)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-1",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          error && "border-[var(--color-danger-500)]",
          className,
        )}
        {...rest}
      >
        <RC.Indicator className="text-[var(--color-text-inverse)]">
          {checked === "indeterminate" ? <Minus size={12} strokeWidth={3} /> : <Check size={12} strokeWidth={3} />}
        </RC.Indicator>
      </RC.Root>
      {(label || hint || error) && (
        <span className="flex flex-col gap-0.5">
          {label && <span className="text-[var(--color-text-primary)]">{label}</span>}
          {hint && !error && <span className="text-xs text-[var(--color-text-secondary)]">{hint}</span>}
          {error && <span className="text-xs text-[var(--color-danger-500)]">{error}</span>}
        </span>
      )}
    </label>
  );
});
